import {
  JustifiPayoutSummaryItem,
  JustifiPayoutSummaryItemType,
  StripePayoutSummaryItem,
  StripePayoutSummaryItemType,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { TFunction } from '@frontend/i18n';
import { TableColumnConfig } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';

const getStripePayoutSummaryItemLabel = (t: TFunction, itemType?: StripePayoutSummaryItemType) => {
  if (!itemType) {
    return 'Unknown';
  }

  const stripeMap = {
    [StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_APPLICATION_FEE]: t('Fees'),
    [StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_ADJUSTMENTS]: t('Adjustments'),
    [StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_CHARGES]: t('Charges'),
    [StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS]: t('Refunds'),
    [StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_UNSPECIFIED]: t('Unknown'),
  } as const;

  const display = stripeMap[itemType];

  if (!display) {
    const message = `Unrecognized itemType received, got ${itemType}`;
    console.error(message);

    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Unrecognized itemType received, got ${itemType}`);
    }
  }
  return display ?? 'Unknown';
};

const getJustifiPayoutSummaryItemLabel = (t: TFunction, itemType?: JustifiPayoutSummaryItemType) => {
  if (!itemType) {
    return 'Unknown';
  }

  const justifiMap = {
    [JustifiPayoutSummaryItemType.JUSTIFI_PAYOUT_SUMMARY_ITEM_TYPE_FEES]: t('Fees'),
    [JustifiPayoutSummaryItemType.JUSTIFI_PAYOUT_SUMMARY_ITEM_TYPE_PAYMENTS]: t('Payments'),
    [JustifiPayoutSummaryItemType.JUSTIFI_PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS]: t('Refunds'),
    [JustifiPayoutSummaryItemType.JUSTIFI_PAYOUT_SUMMARY_ITEM_TYPE_UNSPECIFIED]: t('Unknown'),
  };

  const display = justifiMap[itemType];

  if (!display) {
    const message = `Unrecognized itemType received, got ${itemType}`;
    console.error(message);

    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Unrecognized itemType received, got ${itemType}`);
    }
  }
  return display ?? 'Unknown';
};

export const generateJustifiColumns = (t: TFunction): TableColumnConfig<JustifiPayoutSummaryItem>[] => [
  {
    id: 'type',
    Header: '',
    accessor: (summaryItem) => getJustifiPayoutSummaryItemLabel(t, summaryItem.type),
    maxWidth: 160,
    disableSortBy: true,
    sticky: 'left',
  },
  {
    id: 'count',
    Header: t('Count', { ns: 'payments' }),
    accessor: (summaryItem) => summaryItem.count,
    disableSortBy: true,
  },
  {
    id: 'gross',
    Header: t('Gross', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.gross),
    disableSortBy: true,
  },
  {
    id: 'fees',
    Header: t('Fees', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.fees),
    disableSortBy: true,
  },
  {
    id: 'net',
    Header: t('Net', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.net),
    disableSortBy: true,
  },
];

export const generateStripeColumns = (t: TFunction): TableColumnConfig<StripePayoutSummaryItem>[] => [
  {
    id: 'type',
    Header: '',
    accessor: (summaryItem) => getStripePayoutSummaryItemLabel(t, summaryItem.type),
    maxWidth: 160,
    disableSortBy: true,
    sticky: 'left',
  },
  {
    id: 'count',
    Header: t('Count', { ns: 'payments' }),
    accessor: (summaryItem) => summaryItem.count,
    disableSortBy: true,
  },
  {
    id: 'gross',
    Header: t('Gross', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.gross),
    disableSortBy: true,
  },
  {
    id: 'fees',
    Header: t('Fees', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.fees),
    disableSortBy: true,
  },
  {
    id: 'net',
    Header: t('Net', { ns: 'payments' }),
    accessor: (summaryItem) => formatCentsToCurrency(summaryItem.net),
    disableSortBy: true,
  },
];
